import { SafeSuspense } from '@calendly/safe-suspense'
import { lazy, useEffect } from 'react'
import { createPortal } from 'react-dom'

function LoadFailedFallback({ onClose }) {
  useEffect(() => {
    onClose?.()
  }, [onClose])
  return null
}

// Module federation: oap-web component that renders the import popup (overlay + content)
const RemoteImportContactsModalPaneComponent = lazy(() => import('oap_web/ImportContactsModalPaneComponent')
  .then((module) => {
    const Component = module.default ?? module.ImportContactsModalPaneComponent ?? module
    if (!Component) {
      throw new Error('Invalid component exported from oap_web/ImportContactsModalPaneComponent')
    }
    return { default: Component }
  })
  .catch((error) => {
    console.error('Failed to load import contacts modal pane from oap-web:', error)
    return { default: LoadFailedFallback }
  })
)

/**
 * Local full-screen import contacts modal (no right rail). Renders oap-web modal in a portal.
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {function} props.onClose - Called when the modal should close.
 * @param {string} [props.userLocale] - User locale for the remote modal (e.g. for formatting).
 * @param {function} [props.onUploadInProgressChange] - Callback emitted by remote import flow.
 * @param {string} [props.importEntryPoint] - `'file'` (default), `'google_forms'`, or `'typeform'` for federated routing.
 */
export const ImportContactsModalMFE = ({
  isOpen,
  onClose,
  userLocale,
  onUploadInProgressChange,
  importEntryPoint = 'file'
}) => {
  if (!isOpen) {
    return null
  }

  const modalContent = (
    <SafeSuspense fallback={null}>
      <RemoteImportContactsModalPaneComponent
        onClose={onClose}
        userLocale={userLocale}
        onUploadInProgressChange={onUploadInProgressChange}
        importEntryPoint={importEntryPoint}
      />
    </SafeSuspense>
  )

  return createPortal(modalContent, document.body)
}
